import { useState, useEffect, useMemo } from 'react';
import { X, Copy, Loader2, Plus, Lock, LockOpen, GripVertical } from 'lucide-react';
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../../lib/spizirnaUi';
import Dropdown from './Dropdown';

const BARVA = '#F59E0B';

/**
 * Standardní složení produktu — co na Hawai patří vždycky.
 *
 * Kuchyň podle něj kreslí celou řadu ingrediencí a úpravy (bez ananasu,
 * 2× šunka) jako odchylku od ní; pokladna z něj dělá přepínače „klepnutím
 * odebereš". Ingredience jsou stejné ADD recepty jako přídavky, aby se
 * standard a přídavek daly sečíst.
 *
 * Ukládá se celý seznam při každé změně — pořadí v seznamu je pořadí
 * na kuchyňské kartě; mění se přetažením.
 *
 * Každou ingredienci jde v pokladně odebrat („bez cibule"), i když není
 * v žádné skupině přídavků. Zámek ji proti odebrání zajistí (2026-10-17).
 *
 * Množství (2026-10-23) je volitelná nápověda pro kuchyň („120 g",
 * „2 plátky") — KDS ho ukáže za názvem, nic se z něj nepočítá.
 */
export default function SlozeniProduktu({ produktId, isDark, onZmena }) {
    const [slozeni, setSlozeni] = useState([]);
    const [pridavky, setPridavky] = useState([]);
    const [nacitam, setNacitam] = useState(true);
    const [hlaska, setHlaska] = useState('');

    const mutedColor = isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted)';
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    useEffect(() => {
        let zruseno = false;
        setNacitam(true);
        Promise.all([
            api(`/api/recepty/recepty/${produktId}/slozeni`),
            // Tenhle seznam nese i skupiny přídavků — podle nich se nabídka třídí.
            api('/api/recepty/pridavky'),
        ]).then(([s, p]) => {
            if (zruseno) return;
            setSlozeni(s);
            setPridavky((Array.isArray(p) ? p : p?.data || []).filter(r => r.aktivni !== false));
        }).catch(() => {}).finally(() => { if (!zruseno) setNacitam(false); });
        return () => { zruseno = true; };
    }, [produktId]);

    // Nabídka po skupinách přídavků (Maso, Sýry, Základy…), uvnitř abecedně.
    // Ingredience ve víc skupinách jde pod první; bez skupiny na konec.
    const nabidka = useMemo(() => {
        const uz = new Set(slozeni.map(s => s.pridavek_id));
        const skupiny = new Map();
        pridavky.filter(p => !uz.has(p.id)).forEach(p => {
            const sk = [...(p.pridavky_skupiny || [])].sort((a, b) => (a.poradi ?? 0) - (b.poradi ?? 0))[0];
            const klic = sk ? sk.id : 'bez';
            if (!skupiny.has(klic)) skupiny.set(klic, { nazev: sk?.nazev || 'Bez skupiny', poradi: sk ? (sk.poradi ?? 0) : Infinity, polozky: [] });
            skupiny.get(klic).polozky.push(p);
        });

        return [...skupiny.entries()]
            .sort(([, a], [, b]) => a.poradi - b.poradi || a.nazev.localeCompare(b.nazev, 'cs'))
            .flatMap(([klic, sk]) => [
                { value: `h-${klic}`, label: sk.nazev, hlavicka: true },
                ...sk.polozky
                    .sort((a, b) => a.nazev.localeCompare(b.nazev, 'cs'))
                    .map(p => ({ value: p.id, label: p.nazev })),
            ]);
    }, [pridavky, slozeni]);

    const ulozit = async (nove) => {
        const predtim = slozeni;
        setSlozeni(nove);
        setHlaska('');
        try {
            setSlozeni(await api(`/api/recepty/recepty/${produktId}/slozeni`, 'PUT', {
                polozky: nove.map(s => ({ pridavek_id: s.pridavek_id, ks: s.ks, mnozstvi: s.mnozstvi || null, zamek: !!s.zamek })),
            }));
            onZmena?.();
        } catch (e) {
            setSlozeni(predtim);
            setHlaska(e?.error || e?.message || 'Složení se nepodařilo uložit.');
        }
    };

    const presunout = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const a = slozeni.findIndex(s => s.pridavek_id === active.id);
        const b = slozeni.findIndex(s => s.pridavek_id === over.id);
        if (a < 0 || b < 0) return;
        ulozit(arrayMove(slozeni, a, b));
    };
    const zmenit = (i, zmena) => ulozit(slozeni.map((x, k) => (k === i ? { ...x, ...zmena } : x)));

    const kopirovat = async () => {
        if (!confirm('Přepsat složení ostatních velikostí tohoto produktu tímto seznamem?')) return;
        try {
            const r = await api(`/api/recepty/recepty/${produktId}/slozeni/kopirovat-na-varianty`, 'POST');
            setHlaska(r.zkopirovano?.length
                ? `Zkopírováno na: ${r.zkopirovano.join(', ')}`
                : 'Produkt nemá další velikosti.');
        } catch (e) {
            setHlaska(e?.error || 'Kopírování selhalo.');
        }
    };

    return (
        <div className="rounded-xl px-4 py-3" style={{
            background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)',
            border: `1px solid ${borderColor}`,
        }}>
            <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Složení</span>
                {slozeni.length > 0 && (
                    <button onClick={kopirovat} title="Stejné složení i pro ostatní velikosti (32 cm, 22 cm…)"
                        className="flex items-center gap-1 text-[11px]"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>
                        <Copy size={12} /> na ostatní velikosti
                    </button>
                )}
            </div>
            <p className="text-[11px] mb-3" style={{ color: mutedColor }}>
                Co na jídle standardně je, v pořadí pro kuchyň (přetažením změníš pořadí). Na KDS se ukáže celé
                a úpravy zvýrazněné. V pokladně jde každá ingredience odebrat — zámkem ji proti odebrání zajistíš.
                Co jde přidat navíc, určuje skupina přídavků. Do políčka za názvem můžeš napsat množství
                (např. 120 g) — na KDS se ukáže jako nápověda.
            </p>

            {nacitam ? (
                <Loader2 size={16} className="animate-spin" style={{ color: mutedColor }} />
            ) : (
                <div className="flex flex-wrap items-center gap-1.5">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={presunout}>
                        <SortableContext items={slozeni.map(s => s.pridavek_id)} strategy={rectSortingStrategy}>
                            {slozeni.map((s, i) => (
                                <Cip key={s.pridavek_id} s={s} mutedColor={mutedColor}
                                    onKs={() => zmenit(i, { ks: s.ks >= 3 ? 1 : s.ks + 1 })}
                                    onMnozstvi={m => zmenit(i, { mnozstvi: m })}
                                    onZamek={() => zmenit(i, { zamek: !s.zamek })}
                                    onOdebrat={() => ulozit(slozeni.filter((_, k) => k !== i))} />
                            ))}
                        </SortableContext>
                    </DndContext>

                    <div style={{ minWidth: 220 }}>
                        <Dropdown
                            value=""
                            onChange={id => {
                                const p = pridavky.find(x => x.id === Number(id));
                                if (p) ulozit([...slozeni, { pridavek_id: p.id, nazev: p.nazev, ks: 1 }]);
                            }}
                            options={nabidka}
                            placeholder="Přidat ingredienci…"
                            leftIcon={<Plus size={13} />}
                            isDark={isDark}
                            height={32}
                            accent={BARVA}
                        />
                    </div>
                </div>
            )}

            {hlaska && <p className="text-[11px] mt-2" style={{ color: mutedColor }}>{hlaska}</p>}
        </div>
    );
}

/** Ingredience ve složení — úchytem se přetahuje, zámek ji drží proti odebrání v pokladně. */
function Cip({ s, mutedColor, onKs, onMnozstvi, onZamek, onOdebrat }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.pridavek_id });
    return (
        <span ref={setNodeRef} className="flex items-center gap-0.5 rounded-lg pl-0.5 pr-1 py-1 text-xs font-medium"
            style={{
                transform: CSS.Transform.toString(transform), transition,
                zIndex: isDragging ? 10 : undefined, opacity: isDragging ? 0.85 : 1,
                background: `${BARVA}1A`, border: `1px solid ${isDragging ? BARVA : `${BARVA}55`}`, color: 'var(--text-primary)',
                boxShadow: isDragging ? '0 6px 16px rgba(0,0,0,0.25)' : undefined,
            }}>
            <span {...attributes} {...listeners} title="Přetažením změníš pořadí"
                className="grid place-items-center" style={{ width: 16, height: 18, cursor: 'grab', color: mutedColor, touchAction: 'none' }}>
                <GripVertical size={12} />
            </span>
            <span className="px-1">{s.nazev}</span>
            <PoleMnozstvi hodnota={s.mnozstvi || ''} mutedColor={mutedColor} onUlozit={onMnozstvi} />
            <button title="Kolik kusů je ve standardu (klepnutím změníš)" onClick={onKs}
                className="rounded px-1 text-[10px] font-bold"
                style={{
                    cursor: 'pointer', border: 'none',
                    background: s.ks > 1 ? BARVA : 'transparent',
                    color: s.ks > 1 ? '#111' : mutedColor,
                }}>
                {s.ks}×
            </button>
            <IkonaTlacitko
                title={s.zamek ? 'Zamčeno — v pokladně nejde odebrat (klepnutím odemkneš)' : 'V pokladně jde odebrat (klepnutím zamkneš)'}
                onClick={onZamek} barva={s.zamek ? BARVA : mutedColor}>
                {s.zamek ? <Lock size={12} /> : <LockOpen size={12} />}
            </IkonaTlacitko>
            <IkonaTlacitko title="Odebrat ze složení" onClick={onOdebrat} barva="#EF4444">
                <X size={12} />
            </IkonaTlacitko>
        </span>
    );
}

/**
 * Množství jako krátký volný text. Ukládá se až při opuštění pole (nebo
 * Enter), ne při každém úhozu — každá změna složení jde na server celá.
 */
function PoleMnozstvi({ hodnota, mutedColor, onUlozit }) {
    const [text, setText] = useState(hodnota);
    useEffect(() => { setText(hodnota); }, [hodnota]);
    const ulozit = () => {
        const t = text.trim();
        if (t !== hodnota) onUlozit(t || null);
    };
    return (
        <input
            value={text}
            onChange={e => setText(e.target.value.slice(0, 20))}
            onBlur={ulozit}
            onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') { setText(hodnota); e.currentTarget.blur(); }
            }}
            onPointerDown={e => e.stopPropagation()}
            placeholder="množství"
            title="Množství pro kuchyň, např. 120 g — ukáže se na KDS"
            aria-label="Množství"
            className="rounded px-1 text-[11px]"
            style={{
                width: Math.max(58, Math.min(110, (text.length || 8) * 7 + 12)),
                height: 20, border: `1px solid ${text ? BARVA + '88' : 'transparent'}`,
                background: text ? 'rgba(245,158,11,0.10)' : 'rgba(127,127,127,0.10)',
                color: text ? 'var(--text-primary)' : mutedColor, outline: 'none', fontWeight: text ? 600 : 400,
            }}
        />
    );
}

function IkonaTlacitko({ children, onClick, disabled, title, barva }) {
    return (
        <button title={title} onClick={onClick} disabled={disabled}
            className="grid place-items-center rounded"
            style={{
                width: 18, height: 18, border: 'none', background: 'none', padding: 0,
                cursor: disabled ? 'default' : 'pointer', color: barva, opacity: disabled ? 0.25 : 1,
            }}>
            {children}
        </button>
    );
}
